import {
	Box,
	Button,
	Dialog,
	DialogActions,
	DialogContent,
	FormControl,
	FormControlLabel,
	Grid,
    InputAdornment,
	InputLabel, ListItemText, MenuItem,
	Paper,
	Switch,
	TextField,
	Typography
} from "@mui/material";
import React, {useEffect, useState} from "react";
import {Field, Form, Formik, FormikHelpers} from "formik";
import * as Yup from "yup";
import {NavLink, useNavigate, useParams} from "react-router-dom";
import {toast} from "react-toastify";
import IconButton from "@mui/material/IconButton";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import {useAuth} from "../../../../contexts/auth-context";
import {BaseService} from "../../../../services/base-service";
import {URLS} from "../../../../services/app-urls";
import {User} from "../../../../models/account/user";
import {translate} from "../../../../translate/translate";
import {ErrorComponent} from "../../../../components/error/error.component";
import {useKeepFilters} from "../../../../utils/form-utils";
import Select from "@mui/material/Select";
import {UserService} from "../../../../services/user-service";
import {AccessType} from "../../../../models/account/access-type";
import Autocomplete from '@mui/material/Autocomplete';
import {useFeatureFlags} from "../../../../contexts/feature-flags/feature-flags";
import { Close } from "@mui/icons-material";
import ChangePasswordComponent from "../../../../components/change-password-dialog/change-password.component";

const UserItemComponent = () => {
    const service = new BaseService<User>(URLS.USER);
    const accountService = new UserService();
    const [object, setObject] = React.useState(new User());
    const {user} = useAuth();
    const [is_active, setIsActive] = React.useState(false);
    const [displayContent, setDisplayContent] = useState(true);
    const isCurrentUser = object.id === user?.id;
    const params = useParams();
    const navigate = useNavigate();
    const url = "/registrations/users/";
    const {searchParams} = useKeepFilters();
    const previousUrl = `${url}?${searchParams.toString()}`;
    const [selectedAccess, setSelectedAccess] = useState<string>();
    const [accessTypes, setAccessTypes] = useState<AccessType[]>([]);
    const [remoteUsers, setRemoteUsers] = useState<User[]>([]);
    const features = useFeatureFlags();
	const [open, setOpen] = React.useState(false);
	const [openProceed, setOpenProceed] = React.useState(false);

    const schema = Yup.object().shape({
        name: Yup.string()
            .required(),
        email: Yup.string()
            .required(" ")
            .email(translate.t("invalid_email")),
        username: Yup.string()
            .required(),
        password: Yup.string().when([], {
            is: () => !object.id && !features.authenticationByHUB,
            then: Yup.string().required(),
            otherwise: Yup.string().notRequired(),
        }),
        confirmPassword: Yup.string().when([], {
            is: () => !object.id && !features.authenticationByHUB,
            then: Yup.string().required(),
            otherwise: Yup.string().notRequired(),
        }),
        is_active: Yup.boolean(),
    });

    const handleSubmit = (values: any, {resetForm}: FormikHelpers<User>) => {
        service.clearParameters();
        const user = new User();
        Object.assign(user, values);
        user.is_active = is_active;
        user.perfil = selectedAccess || '';
        if (user.id) {
            try {
                service.patch(user, user.id).then((response) => {
                    toast.success(translate.t("successfully_updated"));
	                setObject(new User());
	                resetForm();
	                setIsActive(false);
	                setDisplayContent(false);
	                navigate(url);
                }).catch((error: any) => {
                    ErrorComponent(error);
                });
            } catch (error) {
                console.log(error);
            }
        } else {
            try {
                service.save(user)
                    .then(response => {
                        toast.success(translate.t("successfully_registered"));
	                    setObject(new User());
	                    resetForm();
	                    setIsActive(false);
	                    setDisplayContent(false);
	                    navigate(url);
                    })
                    .catch((error: any) => {
                        ErrorComponent(error);
                    });
            } catch (error) {
                console.log(error);
            }
        }
    };

    function onSubmit(values: any, resetForm: any) {
        service.clearParameters();
        if (params["action"] !== "create") {
            service.getById(parseInt(params["action"] as string)).then((response) => {
                setObject(response.data);
            });
            handleSubmit(values, resetForm);
        } else {
            if (values.password !== values.confirmPassword) {
                toast.error(translate.t("password_not_match"));
            } else {
                handleSubmit(values, resetForm);
            }
        }
    }

    useEffect(() => {
        accountService.listGroups().then((response: any) => {
            setAccessTypes(response.data);
        })
        if (params["action"] !== "create") {
            service.getById(parseInt(params["action"] as string)).then((response) => {
                setObject(response.data);
                setSelectedAccess(response.data.perfil || '');
                setIsActive(response.data.is_active);
            });
        } else if (features.authenticationByHUB) {
	        accountService.getRemoteUsers().then((response: any) => {
		        setRemoteUsers(response.data);
	        });
        }
    }, []);

    const handleChangeActive = (newValue: boolean) => {
        setIsActive(newValue);
    };

	const customFilterOptions = (options: User[], { inputValue }: any) => {
		const searchTerms = inputValue.toLowerCase();
		return options.filter((option: User) => {
			return option.name.toLowerCase().includes(searchTerms) ||
				option.username.toLowerCase().includes(searchTerms) ||
				option.email.toLowerCase().includes(searchTerms)
		});
	};

	const handleOpenDialog = () => {
		setOpen(true);
	};

	const handleCloseDialog = () => {
		setOpen(false);
	};

	const handleOpenDialogProceed = () => {
		setOpenProceed(true);
	};

	const handleCloseDialogProceed = () => {
		setOpenProceed(false);
	};

	return (
		<section>
			<div className="floating-title floating-title-item"
			     style={{display: "flex", flexDirection: "row", alignItems: "center"}}>
				<IconButton
					component={NavLink}
					to={previousUrl}>
					<ArrowBackIcon color="secondary" fontSize="large" fontWeight="fontWeightBold"/>
				</IconButton>
				<Typography variant={"h5"}>
					{params["action"] !== "create" ? 'Editar usuário' : 'Novo usuário'}
				</Typography>
			</div>
			<div>
				<Paper variant="outlined">
					<Box p={2}>
						<Typography variant={"h5"} fontSize="16px" mb="10px">
							Dados de login
						</Typography>
						{params["action"] == "create" && features.authenticationByHUB && (
							<Typography fontSize="14px" mt="15px" mb="15px">
								Busque um usuário já registrado no hub corporativo Foxconn.
							</Typography>
						)}
						<Formik
							enableReinitialize
							initialValues={object}
							onSubmit={onSubmit}
							validationSchema={schema}>
							{
								({isValid, values, isValidating, errors, touched}) => (
									<Form autoComplete="off">
										<Grid container gap={2} direction={"column"}>
											{features.authenticationByHUB && (
												<Grid container spacing={2} direction={"row"} width="60%">
													<Grid>
														{params["action"] == "create" && (
															<Box>
																<Autocomplete
																	disablePortal
																	id="remote-users"
																	options={remoteUsers}
																	getOptionLabel={(option: any) => option.name}
																	renderOption={(props, option) => (
																		<li {...props} key={option.username}>
																			<div>
																				<span style={{padding: "4px", color: "blue"}}>{option.name}</span>|
																				<span style={{padding: "4px"}}>{option.username}</span>|
																				<span style={{padding: "4px"}}>{option.email}</span>
																			</div>
																		</li>
																	)}
																	filterOptions={customFilterOptions}
																	onChange={(event, newValue) => {
																		if (newValue) {
																			setObject((prev) => ({
																				...prev,
																				username: newValue.username,
																				name: newValue.name,
																				email: newValue.email,
																			}));
																		}
																	}}
																	renderInput={(params: any) =>
																		<TextField {...params} label="Buscar usuário por nome, usuário ou e-mail"/>}
																/>
															</Box>
														)}
													</Grid>
												</Grid>
											)}

											<Grid container spacing={2} direction={"row"} width="60%">
												<Grid >
													<Field
														as={TextField}
														name="name"
														label={translate.t("complete_name")}
														variant={"outlined"}
														fullWidth
														autoFocus={false}
														values={values}
														type={"name"}
														disabled={features.authenticationByHUB}
													/>
												</Grid>
												<Grid >
													<Field
														as={TextField}
														name="username"
														label={translate.t("username")}
														variant={"outlined"}
														fullWidth
														autoFocus={true}
														values={values}
                                                        disabled={features.authenticationByHUB}
													/>
												</Grid>
												<Grid >
													<Field
														as={TextField}
														name="email"
														label={translate.t("email")}
														variant={"outlined"}
														fullWidth
														autoFocus={false}
														values={values}
														type={"email"}
                                                        disabled={features.authenticationByHUB}
													/>
												</Grid>
											</Grid>

											{!object.id && !features.authenticationByHUB && (
												<Grid container spacing={2} direction={"row"}>
													<Grid size={12} >
														<Field
															as={TextField}
															name="password"
															label={translate.t("password")}
															variant={"outlined"}
															fullWidth
															autoFocus={false}
															required={true}
															values={values}
															type={"password"}
															error={touched.password && Boolean(errors.password)}
															InputProps={{
															maxLength: 104,
															endAdornment: (
																<InputAdornment position="end">
																{touched.password && Boolean(errors.password) && (
																	<div className={"required-field"}>
																		{translate.t("required_field")}
																	</div>
																)}
																</InputAdornment>
															),
														}}/>
													</Grid>
													<Grid size={12}>
														<Field
															as={TextField}
															name="confirmPassword"
															label={translate.t("confirmPassword")}
															variant={"outlined"}
															fullWidth
															autoFocus={false}
															required={true}
															values={values}
															type={"password"}
															error={touched.confirmPassword && Boolean(errors.confirmPassword)}
															InputProps={{
															maxLength: 104,
															endAdornment: (
																<InputAdornment position="end">
																{touched.confirmPassword && Boolean(errors.confirmPassword) && (
																	<div className={"required-field"}>
																		{translate.t("required_field")}
																	</div>
																)}
																</InputAdornment>
															),
														}}/>
													</Grid>
												</Grid>
											)}
											<Typography variant={"h5"} fontSize="16px" mt="20px">
												Situação
											</Typography>

											<Grid alignItems={"center"}>
												<FormControlLabel
													control={
														<Field
															type="checkbox"
															name="active"
															component={Switch}
															checked={is_active}
															onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
																handleChangeActive(e.target.checked)}
															color="secondary"
															disabled={isCurrentUser}
														/>
													}
													label={is_active ? translate.t("active") : translate.t("inactive")}
												/>
											</Grid>

											<Typography variant={"h5"} fontSize="16px" mt="20px" mb="10px">
												Permissões de acesso
											</Typography>

											<Grid size={4}>
												<FormControl sx={{width: '400px'}}>
													<InputLabel>
														{translate.t("user_type")}
													</InputLabel>
													<Field
														component={Select}
														label={translate.t("user_type")}
														variant={"outlined"}
														fullWidth
														autoFocus={false}
														value={selectedAccess || ''}
														onChange={(event: any) => {
															setSelectedAccess(event.target.value);
														}}
														MenuProps={{
															PaperProps: {
																style: {
																	maxHeight: '400px',
																},
															},
															disableAutoFocusItem: true,
														}}
														inputProps={{
															sx: {
																maxWidth: '400px !important',
																width: '400px !important',
															}
														}}
													>
														{accessTypes?.map((p: any) => (
															<MenuItem key={p.name} value={p.name}>
																<ListItemText primary={p.name}/>
															</MenuItem>
														))}
													</Field>
												</FormControl>
											</Grid>

											<Grid
												container size={12} spacing={1}
												justifyContent={"flex-end"}>
												{object.id &&
                                                    <Grid >
                                                        <Button
                                                            color="primary"
                                                            variant="contained"
                                                            onClick={() => handleOpenDialog()}>
															{translate.t("redefine_password")}
                                                        </Button>
                                                    </Grid>}
												<Grid >
													<Button
														color="secondary"
														component={NavLink}
														to={previousUrl}
														variant="outlined">
														{translate.t("cancel")}
													</Button>
												</Grid>
												<Grid >
													<Button
														color="primary"
														variant="contained"
														type="submit"
														name="save_form"
														disabled={!isValid || isValidating || !values.name || !selectedAccess}>
														{translate.t("save")}
													</Button>
												</Grid>
											</Grid>
										</Grid>
									</Form>
								)}
						</Formik>
					</Box>
				</Paper>
				<Dialog open={open} onClose={handleCloseDialog}>
					<Grid padding={2} container justifyContent="space-between" sx={{backgroundColor: "primary.main"}}>
						<Grid >
							<Typography sx={{color: "#ffffff", fontWeight: "bold", fontSize: "16px"}}>
								{translate.t("confirm_recover_password_tittle")}
							</Typography>
						</Grid>
						<Grid sx={{cursor: "pointer", color: "#ffffff"}}>
							<Close onClick={() => {
								handleCloseDialog();
							}}/>
						</Grid>
					</Grid>
					<DialogContent>
						<Grid container flexDirection="column" gap={3}>
							<Grid >
								<Typography>
									{translate.t("confirm_recover_password_text")}
								</Typography>
							</Grid>
						</Grid>
					</DialogContent>
					<DialogActions>
						<Grid container justifyContent="flex-end" gap={2}>
							<Grid >
								<Button
									color="primary"
									variant="outlined"
									onClick={() => {
										handleCloseDialog();
									}}>
									{translate.t("cancel")}</Button>
							</Grid>
							<Grid >
								<Button
									variant="contained"
									color="primary"
									onClick={() => {
										handleOpenDialogProceed();
										handleCloseDialog();
									}}>
									{translate.t("proceed")}
								</Button>
							</Grid>
						</Grid>
					</DialogActions>
				</Dialog>
				<ChangePasswordComponent
					openProceed={openProceed} object={object}
					handleCloseDialogProceed={handleCloseDialogProceed}/>
			</div>
		</section>
	);

};

export default UserItemComponent;
